import { Component, Input, Optional, ViewEncapsulation, forwardRef, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, NG_VALIDATORS, AbstractControl, ValidationErrors, ControlContainer } from '@angular/forms';
import { Cross } from '../icon/icon-map';
import mixinDisabled from '../../mixins/disabled';
import { NEW_PHONE_REGEX, NOT_DIGITS, PHONE, PHONE_DIGITS_ONLY } from '../../validators/validator-regex';
import { PhxTokenizeInputValidatorType } from './phx-tokenize-input.type';

const baseInputMixin = mixinDisabled(
  class {
    constructor() {}
  }
);

@Component({
  selector: 'phx-tokenize-input',
  templateUrl: './phx-tokenize-input.component.html',
  styleUrls: ['./phx-tokenize-input.component.scss'],
  host: {
    class: 'phx-tokenize-input',
    '[class.phx-tokenize-input-disabled]': 'phxDisabled',
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  inputs: ['phxDisabled'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhxTokenizeInputComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhxTokenizeInputComponent),
      multi: true
    }
  ]
})
export class PhxTokenizeInputComponent extends baseInputMixin implements ControlValueAccessor, OnInit {
  @Input() phxTokenizeInputValidator: PhxTokenizeInputValidatorType;
  @Input() phxCustomValidator: ((tag: string) => string) | null = null;
  @Input() formControlName: string;
  @Input() phxDisabled: boolean = false;
  @Input() phxPlaceholder: string;

  public tags: string[] = [];
  public newTag: string = '';
  public CrossIcon: string = Cross;
  public control: AbstractControl | null = null;

  constructor(@Optional() private _controlContainer: ControlContainer) {
    super();
  }

  public ngOnInit(): void {
    if (this._controlContainer && this.formControlName) {
      this.control = this._controlContainer.control?.get(this.formControlName);
    } else {
      this.control = new FormControl([], this.validate.bind(this));
    }
  }

  private onChange = (tags: string[]) => {};
  private onTouched = () => {};

  public writeValue(tags: string[]): void {
    if (tags) {
      this.tags = tags;
    } else {
      this.tags = [];
    }
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public addTag(): void {
    const tagToAdd = this.newTag.trim();
    if (tagToAdd) {
      this.tags = [...this.tags, tagToAdd];
      this.newTag = '';
      this.onChange(this.tags);
      if (this.control) {
        this.control.setValue(this.tags);
      }
    }
    this.onTouched();
  }

  public removeTag(index: number): void {
    this.tags = this.tags.filter((_, i) => i !== index);
    this.onChange(this.tags);
    if (this.control) {
      this.control.setValue(this.tags);
    }
  }

  public handleBlur(): void {
    if (this.control) {
      this.control.markAsTouched(); // Mark control as touched on blur
    }
    this.onTouched(); // Call onTouched
  }

  public isValidTag(tag: string): string {
    if (this.phxCustomValidator) {
      return this.phxCustomValidator(tag);
    }

    switch (this.phxTokenizeInputValidator) {
      case 'email':
        return this.validateEmail(tag);
      case 'phonenumber':
        return this.validatePhoneNumber(tag);
      default:
        return null;
    }
  }

  public tagText(tag: string) {
    if (this.phxTokenizeInputValidator === 'phonenumber') {
      return this.formatPhoneNumber(tag);
    }
    return tag;
  }

  public validate(control: AbstractControl): ValidationErrors | null {
    if (!this.control?.value) return;
    for (let tag of control.value) {
      const validateMessage = this.isValidTag(tag);
      if (validateMessage) {
        return { 'invalidTag': validateMessage };
      }
    }
    return null;
  }

  public handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ',' || event.key === 'Tab') {
      event.preventDefault();
      this.addTag();
    }
    if(event.key === 'Backspace' && this.newTag === '') {
      this.tags.pop();
      this.onChange(this.tags);
      if (this.control) {
        this.control.setValue(this.tags);
      }
    }
  }
  private validateEmail(tag: string): string {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(tag) ? null : 'Should be valid email';
  }

  private validatePhoneNumber(phoneNumber: string): string {
    const formattedNumber = this.formatPhoneNumber(phoneNumber);
    return (!!formattedNumber.match(PHONE) || !!formattedNumber.match(NEW_PHONE_REGEX)) ? null : 'number is not correct';
  }

  private formatPhoneNumber(value: string): string {
    const numberArray = value.replace(NOT_DIGITS, '').match(PHONE_DIGITS_ONLY)?.filter(phoneNumber => phoneNumber);
    if (numberArray) {
      let phoneNumber = numberArray[1] + '-' + numberArray[2] + '-' + numberArray[3];
      if (numberArray.length === 5) {
        phoneNumber += '-' + numberArray[4];
      }
      return phoneNumber;
    }
    return value;
  }
}
